import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "../componants/common/Toast.css";

const ToastContext = createContext({
  showToast: () => {},
});

let toastId = 0;

export function inferToastTone(message) {
  const text = String(message || "").trim();
  if (/^(تم[\s\u00A0]|نجح)|بنجاح/.test(text)) return "success";
  return "error";
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (message, tone = "error") => {
      const text = Array.isArray(message)
        ? message.filter(Boolean).join("\n")
        : String(message ?? "").trim();
      if (!text) return;

      const id = ++toastId;
      setToasts((prev) => [...prev.slice(-4), { id, text, tone }]);
      const timer = window.setTimeout(() => dismiss(id), 4200);
      timersRef.current.set(id, timer);
    },
    [dismiss],
  );

  useEffect(() => {
    const originalAlert = window.alert;
    window.alert = (message) => {
      const text = Array.isArray(message)
        ? message.filter(Boolean).join("\n")
        : String(message ?? "");
      showToast(text, inferToastTone(text));
    };
    return () => {
      window.alert = originalAlert;
      timersRef.current.forEach((timer) => window.clearTimeout(timer));
      timersRef.current.clear();
    };
  }, [showToast]);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="ess-toast-stack" aria-live="polite" aria-relevant="additions">
        {toasts.map((toast) => (
          <button
            key={toast.id}
            type="button"
            className={`ess-toast ess-toast-${toast.tone}`}
            onClick={() => dismiss(toast.id)}
          >
            {toast.text}
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

export default ToastContext;
